import LaunchIcon from "@mui/icons-material/Launch";
import MinimizeIcon from "@mui/icons-material/Minimize";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Fade,
  IconButton,
  Paper,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ASSISTANT_ROUTE } from "@helpers/constants";
import { ASSISTANT_EVENTS } from "@helpers/events";

import { useSharedAssistantChat } from "../context/AssistantChatContext";
import MessageList from "./MessageList";

const WindowContainer = styled(Paper)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  width: "40vw",
  height: "60vh",
  minWidth: "350px",
  minHeight: "400px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  zIndex: 1000,
  boxShadow: theme.shadows[10],
  borderRadius: theme.shape.borderRadius,
}));

const WindowHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
}));

const WindowContent = styled(Box)({
  flex: 1,
  overflow: "hidden",
});

const ChatContainer = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: theme.spacing(1),
  height: "100%",
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1),
  gap: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  paddingBottom: theme.spacing(1),
  paddingRight: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

interface FloatingAssistantWindowProps {
  isOpen: boolean;
  onMinimize: () => void;
}

const FloatingAssistantWindow: React.FC<FloatingAssistantWindowProps> = ({
  isOpen,
  onMinimize,
}) => {
  const navigate = useNavigate();

  const {
    messages,
    inputText,
    setInputText,
    isLoading,
    createNewSession,
    handleSendMessage,
    handleKeyPress,
    retryLastRequest,
    useComponentRefs,
  } = useSharedAssistantChat();

  const { messagesEndRef, messagesContainerRef, inputRef, handleScroll } =
    useComponentRefs("floating-assistant");

  const handleSendMessageForFloating = () => {
    tracker.event(ASSISTANT_EVENTS.MESSAGE_SENT);
    handleSendMessage("floating-assistant");
  };
  const handleKeyPressForFloating = (e: React.KeyboardEvent) => {
    handleKeyPress(e as any, "floating-assistant");
  };
  const handleCreateNewSession = () => {
    tracker.event(ASSISTANT_EVENTS.NEW_CONVERSATION_STARTED);
    createNewSession("floating-assistant");
  };

  const handleMaximize = () => {
    tracker.event(ASSISTANT_EVENTS.FLOATING_WINDOW_MAXIMIZED);
    onMinimize();
    navigate(ASSISTANT_ROUTE);
  };

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && messages.length === 0) {
      hasInitialized.current = true;
      createNewSession("floating-assistant");
    }
    // eslint-disable-next-line
  }, []); // On initial mount, start new session

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, inputRef]);

  const handleTransitionEnd = () => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit timeout={300}>
      <Fade in={isOpen} timeout={400} onEntered={handleTransitionEnd}>
        <WindowContainer>
          <WindowHeader>
            <Typography variant="subtitle1" fontWeight="bold">
              Assistant
            </Typography>
            <Box>
              <Tooltip title="Maximize">
                <IconButton
                  size="small"
                  onClick={handleMaximize}
                  sx={{ color: "white" }}
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={onMinimize}
                sx={{ color: "white" }}
              >
                <MinimizeIcon fontSize="small" />
              </IconButton>
            </Box>
          </WindowHeader>
          <WindowContent>
            <ChatContainer elevation={3}>
              <HeaderContainer>
                <Tooltip title="New Conversation">
                  <IconButton
                    onClick={handleCreateNewSession}
                    color="primary"
                    size="small"
                    aria-label="new conversation"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </HeaderContainer>
              <MessageList
                messages={messages}
                isLoading={isLoading}
                messagesEndRef={messagesEndRef}
                messagesContainerRef={messagesContainerRef}
                handleScroll={handleScroll}
                retryLastRequest={retryLastRequest}
                isCompactMode={true}
              />
              <InputContainer>
                <TextField
                  fullWidth
                  placeholder="Type a message..."
                  multiline
                  maxRows={4}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPressForFloating}
                  disabled={isLoading}
                  size="small"
                  variant="outlined"
                  inputRef={inputRef}
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessageForFloating}
                  disabled={!inputText.trim() || isLoading}
                  size="small"
                >
                  <SendIcon />
                </IconButton>
              </InputContainer>
            </ChatContainer>
          </WindowContent>
        </WindowContainer>
      </Fade>
    </Slide>
  );
};

export default FloatingAssistantWindow;
